import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Store } from 'lucide-react'
import { getPlans } from '../api/auth'
import { PublicFooter, PublicHeader } from '../components/PublicLayout'
import { PricingPlans, PricingQuestions } from './Pricing'

// The landing page: one promise and one way in, then the plans and their
// prices, and a few questions. /pricing comes here too (#pricing).

/** A small, made-up Overview, to show what the app looks like. */
function OverviewPreview() {
  return (
    <div className="lp-preview" aria-hidden="true">
      <div className="lp-preview-title">
        <span className="pub-brand-mark"><Store size={15} /></span>
        <div><strong>Corner Market</strong><span>Friday, October 16</span></div>
      </div>
      <div className="lp-preview-tiles">
        <div><span>Cash on hand</span><strong>48,250.00</strong><em>Across your cash and bank accounts</em></div>
        <div><span>Sales today</span><strong>1,320.00</strong><em className="up">12% up on yesterday</em></div>
        <div><span>Running low</span><strong>3</strong><em className="warn">At or below reorder level</em></div>
      </div>
    </div>
  )
}

export default function Home() {
  const [plans, setPlans] = useState(null)
  const [error, setError] = useState('')
  const { hash } = useLocation()
  useEffect(() => {
    getPlans().then(setPlans).catch(() => setError('Prices couldn’t load. Refresh the page to try again.'))
  }, [])
  // Arriving at /#pricing (or from /pricing): go to the plans once they're on the page.
  useEffect(() => {
    if (hash !== '#pricing' || !plans) return
    document.getElementById('pricing')?.scrollIntoView()
  }, [hash, plans])

  return (
    <div className="pub">
      <PublicHeader>
        <a className="pub-btn pub-btn-text" href="#pricing">Pricing</a>
        <Link className="pub-btn pub-btn-text" to="/login">Sign in</Link>
        <Link className="pub-btn pub-btn-dark" to="/signup">Start free trial</Link>
      </PublicHeader>

      <main>
        <section className="lp-hero pub-wrap">
          <div className="lp-hero-text">
            <h1>Sell, bill and <span>get paid.</span></h1>
            <p>Invoices, point of sale and jobs, in one app.</p>
            <div className="lp-actions">
              <Link className="pub-btn pub-btn-primary pub-btn-lg" to="/signup">Start free trial</Link>
              <a className="pub-btn pub-btn-ghost pub-btn-lg" href="#pricing">See pricing</a>
            </div>
            <p className="lp-small">30 days free · No card needed · Cashiers always free</p>
          </div>
          <OverviewPreview />
        </section>

        <section className="pub-wrap lp-section pr-section" id="pricing" aria-labelledby="lp-pricing">
          <h2 id="lp-pricing">Choose your plan</h2>
          <p className="lp-section-intro">Pick the kind of business you run, then the plan. Every plan starts with a 30-day free trial.</p>
          <PricingPlans data={plans} error={error} />
        </section>

        <section className="pub-wrap lp-section" aria-labelledby="lp-questions">
          <h2 id="lp-questions">Questions</h2>
          <PricingQuestions />
        </section>

        <section className="pub-wrap lp-section" aria-label="For accountants">
          <div className="lp-accountants">
            <div>
              <strong>An accountant or bookkeeper?</strong>
              <p>Look after all your clients’ books from one free practice account.</p>
            </div>
            <Link className="pub-btn pub-btn-ghost" to="/accountants">ASYS for accountants</Link>
          </div>
        </section>

        <section className="pub-wrap lp-section">
          <div className="lp-band">
            <h2>Try it free for 30 days.</h2>
            <div className="lp-actions lp-actions-center">
              <Link className="pub-btn pub-btn-white pub-btn-lg" to="/signup">Start free trial</Link>
            </div>
          </div>
        </section>
      </main>

      <PublicFooter>
        <a href="#pricing">Pricing</a>
        <Link to="/accountants">For accountants</Link>
        <Link to="/login">Sign in</Link>
      </PublicFooter>
    </div>
  )
}
